import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useWaitlistCTA } from "../lib/useWaitlistCTA";

/**
 * Slim, mobile-only sticky CTA above the footer.
 *
 * Rules:
 *  - Only renders on screens < 768px
 *  - Hidden on /thank-you, /referral, /privacy
 *  - Appears after the visitor scrolls past the hero (200px)
 *  - Hides while the waitlist form section (#waitlist) is in view
 *  - Hides near the footer (last 220px of the page)
 */
const SUPPRESSED_ROUTES = ["/thank-you", "/referral", "/privacy"];
const SHOW_AFTER_PX = 200;
const FOOTER_HIDE_PX = 220;

const MobileStickyCTA = () => {
    const { pathname } = useLocation();
    const [visible, setVisible] = useState(false);
    const { openWaitlist } = useWaitlistCTA();

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        if (SUPPRESSED_ROUTES.includes(pathname)) {
            setVisible(false);
            return undefined;
        }

        const compute = () => {
            const isSmall = window.matchMedia("(max-width: 767px)").matches;
            if (!isSmall) {
                setVisible(false);
                return;
            }

            const scrolled = window.scrollY || window.pageYOffset || 0;
            const doc = document.documentElement;
            const distFromBottom = doc.scrollHeight - (scrolled + window.innerHeight);

            // Waitlist form in viewport
            let waitlistInView = false;
            const waitlistEl = document.getElementById("waitlist");
            if (waitlistEl) {
                const r = waitlistEl.getBoundingClientRect();
                waitlistInView = r.top < window.innerHeight - 40 && r.bottom > 80;
            }

            const show =
                scrolled > SHOW_AFTER_PX &&
                distFromBottom > FOOTER_HIDE_PX &&
                !waitlistInView;
            setVisible(show);
        };

        compute();
        window.addEventListener("scroll", compute, { passive: true });
        window.addEventListener("resize", compute);
        return () => {
            window.removeEventListener("scroll", compute);
            window.removeEventListener("resize", compute);
        };
    }, [pathname]);

    if (!visible) return null;

    return (
        <div
            data-testid="mobile-sticky-cta"
            className="md:hidden fixed bottom-3 inset-x-3 z-40 animate-fade-up"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
            <button
                type="button"
                onClick={openWaitlist}
                data-testid="mobile-sticky-cta-link"
                className="w-full flex items-center justify-between gap-3 rounded-2xl bg-brand-coral text-white shadow-hover px-4 py-3 ring-1 ring-brand-coral/40"
            >
                <span className="flex flex-col text-left leading-tight">
                    <span className="font-heading text-[15px] font-bold">Join the waitlist</span>
                    <span className="text-[11.5px] text-white/85 mt-0.5">
                        Early access through WhatsApp
                    </span>
                </span>
                <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/15">
                    <ArrowRight size={17} strokeWidth={2.25} />
                </span>
            </button>
        </div>
    );
};

export default MobileStickyCTA;
